import React, { useEffect, useState } from 'react';
import { QueryResult, useQuery } from '@apollo/client';
import { ProxyGraphClients, abgGraphqlClient } from '@atlassian/temp-client-utils';
import { RecentlyViewedData } from '@atlassian/cssxp-typescommon';
import { RecentlyViewedCustomerAddressMetadata } from '@css-xp/ppc-types';
import {
  FetchShipToPartyForWidgetInPpcQuery,
  FetchShipToPartyForWidgetInPpcQueryVariables,
  CommerceAddress,
} from '@css-xp/ppc-graphtypes';

import { fetchShipToPartyForWidgetInPPC } from '../../../../../../queries';
import { MAX_WIDGET_ITEMS } from '../../../constants/widget';

export type CustomerAddressQueryItem = Pick<RecentlyViewedData<RecentlyViewedCustomerAddressMetadata>, 'timestamp'> &
  Pick<CommerceAddress, 'name' | 'id' | 'addressLine1' | 'addressLine2' | 'city' | 'state' | 'country' | 'postcode'> & {
    transactionAccountId: string;
  };

type ChildrenProp = (
  args: Omit<QueryResult<FetchShipToPartyForWidgetInPpcQuery, FetchShipToPartyForWidgetInPpcQueryVariables>, 'data'> & {
    data: CustomerAddressQueryItem[] | null | undefined;
  }
) => JSX.Element;

export const CustomerAddressQueryWrapper = ({
  txaId,
  recentlyViewedData,
  children,
}: {
  txaId: string;
  recentlyViewedData: RecentlyViewedData<RecentlyViewedCustomerAddressMetadata>[];
  children: ChildrenProp;
}): JSX.Element => {
  const ids: string[] = [];
  const [recentlyViewedWidgetData, setRecentlyViewedWidgetData] = useState<CustomerAddressQueryItem[]>();
  recentlyViewedData.forEach((data) => {
    if (data?.metadata?.id) {
      ids.push(data.metadata.id);
    }
  });
  const ppcBffGraphqlClient = React.useMemo(() => abgGraphqlClient(ProxyGraphClients.PPC_BFF), []);
  const { data, ...otherArgs } = useQuery<
    FetchShipToPartyForWidgetInPpcQuery,
    FetchShipToPartyForWidgetInPpcQueryVariables
  >(fetchShipToPartyForWidgetInPPC, {
    fetchPolicy: 'cache-and-network',
    client: ppcBffGraphqlClient,
    variables: {
      id: txaId,
      shipToPartyOptions: {
        filterOptions: { ids: ids },
        pageOptions: { size: MAX_WIDGET_ITEMS },
      },
    },
    skip: !ids.length,
  });

  useEffect(() => {
    const obj: Record<string, any> = {};
    const addressObj = data?.transactionAccount?.shipToParty?.nodes?.reduce((obj, node) => {
      if (node.id) {
        obj[node.id] = node;
      }
      return obj;
    }, obj);
    const enrichedRecentlyViewedData: CustomerAddressQueryItem[] = recentlyViewedData.map((data) => {
      const id = data?.metadata?.id;
      if (id && addressObj) {
        return {
          id: id,
          transactionAccountId: txaId,
          timestamp: data.timestamp,
          ...addressObj[id],
        } as CustomerAddressQueryItem;
      } else {
        return {
          transactionAccountId: txaId,
          timestamp: data.timestamp,
          name: data?.metadata?.name,
        } as CustomerAddressQueryItem;
      }
    });
    setRecentlyViewedWidgetData(enrichedRecentlyViewedData);
  }, [data, recentlyViewedData]);

  return children({
    ...otherArgs,
    data: recentlyViewedWidgetData,
  });
};
